import React, { useState } from 'react';
import { orderService } from '../services/api';

function OrderList({ orders, isDeposito, isSupermercado, onStatusUpdate }) {
  const [expandedOrders, setExpandedOrders] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmedQuantities, setConfirmedQuantities] = useState({});
  const [showConfirmedOrder, setShowConfirmedOrder] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDIENTE':
        return 'Pendiente';
      case 'EN_PROCESO':
        return 'En Proceso';
      case 'ENVIADA':
        return 'Enviada';
      case 'RECIBIDA':
        return 'Recibida';
      default:
        return status;
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const loadHistory = async (orderId) => {
    if (historyData[orderId]) {
      return;
    }
    
    setLoadingHistory(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await orderService.getOrderHistory(orderId);
      if (response.success) {
        setHistoryData(prev => ({ ...prev, [orderId]: response.data || [] }));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const toggleHistory = (orderId) => {
    if (!historyData[orderId]) {
      loadHistory(orderId);
    }
  };

  const getTotalItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + item.cantidad, 0);
  };

  const getTotalConfirmedItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + (item.cantidadConfirmada || 0), 0);
  };

  const getDiffClassName = (diff) => {
    if (diff < 0) return 'diff-negative';
    if (diff > 0) return 'diff-positive';
    return '';
  };

  const formatDiff = (diff) => {
    return diff > 0 ? `+${diff}` : diff.toString();
  };

  const openConfirmModal = (order) => {
    const initialQuantities = {};
    order.items.forEach(item => {
      initialQuantities[item.id] = item.cantidad;
    });
    setConfirmedQuantities(initialQuantities);
    setConfirmModal(order);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
    setConfirmedQuantities({});
  };

  const handleQuantityChange = (itemId, value) => {
    // Allow empty string for editing
    if (value === '') {
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: ''
      }));
    } else {
      const numValue = parseInt(value, 10);
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: isNaN(numValue) ? 0 : Math.max(0, numValue)
      }));
    }
  };

  const handleConfirmAndSend = async () => {
    if (!confirmModal) return;
    
    const confirmedItems = Object.entries(confirmedQuantities).map(([itemId, cantidad]) => ({
      itemId: parseInt(itemId, 10),
      cantidadConfirmada: cantidad
    }));
    
    await onStatusUpdate(confirmModal.id, 'ENVIADA', confirmedItems);
    closeConfirmModal();
  };

  const toggleConfirmedOrder = (orderId) => {
    setShowConfirmedOrder(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const hasConfirmedQuantities = (order) => {
    return order.items && order.items.some(item => item.cantidadConfirmada !== null && item.cantidadConfirmada !== undefined);
  };

  if (orders.length === 0) {
    return (
      <div className="empty-state">
        <p>No hay órdenes para mostrar</p>
      </div>
    );
  }

  return (
    <div className="orders-list">
      {orders.map((order) => (
        <div key={order.id} className="order-card">
          <div className="order-header" onClick={() => toggleExpand(order.id)}>
            <div className="order-main-info">
              <span className="order-id">Orden #{order.id}</span>
              <span className={`status-badge status-${order.status}`}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <div className="order-summary">
              <span className="order-items-count">
                {order.items?.length || 0} producto(s) - {getTotalItems(order.items)} unidad(es)
              </span>
              <span className="expand-icon">{expandedOrders[order.id] ? '▼' : '▶'}</span>
            </div>
          </div>
          
          <div className="order-meta">
            {order.supermercadoNombre && (
              <span className="supermercado-highlight">
                <strong>Supermercado:</strong> {order.supermercadoNombre}
              </span>
            )}
            <span>Creado por: {order.createdByNombre || order.createdByUsername}</span>
            <span>Creado: {formatDate(order.createdAt)}</span>
          </div>
          
          {order.statusChangedAt && (
            <div className="order-status-date">
              <span>Estado actualizado: {formatDate(order.statusChangedAt)}</span>
            </div>
          )}
          
          <div className="order-description">
            <strong>Descripción:</strong> {order.descripcion}
          </div>
          
          <div className="order-actions">
            <button 
              onClick={async () => {
                try {
                  await orderService.downloadOrderPdf(order.id);
                } catch (err) {
                  console.error('Error downloading PDF:', err);
                  alert('Error al descargar el PDF');
                }
              }} 
              className="btn btn-small btn-primary"
              style={{ marginRight: '10px' }}
            >
              📄 Descargar PDF
            </button>
          </div>

          {expandedOrders[order.id] && order.items && order.items.length > 0 && (
            <div className="order-items-detail">
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.codigoBarras}</td>
                      <td>{item.nombreProducto}</td>
                      <td>{item.cantidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Confirmed Order Document for ENVIADA/RECIBIDA status */}
          {(order.status === 'ENVIADA' || order.status === 'RECIBIDA') && hasConfirmedQuantities(order) && (
            <div className="confirmed-order-section">
              <button 
                onClick={() => toggleConfirmedOrder(order.id)} 
                className="btn btn-small btn-info"
              >
                {showConfirmedOrder[order.id] ? 'Ocultar Orden Confirmada' : 'Ver Orden Confirmada'}
              </button>
              
              {showConfirmedOrder[order.id] && (
                <div className="confirmed-order-document">
                  <h4>📋 Documento de Orden Confirmada</h4>
                  <p className="confirmed-order-subtitle">Cantidades reales enviadas por el depósito</p>
                  <table className="confirmed-items-table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Producto</th>
                        <th>Cant. Pedida</th>
                        <th>Cant. Enviada</th>
                        <th>Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item, index) => {
                        const diff = (item.cantidadConfirmada || 0) - item.cantidad;
                        return (
                          <tr key={index} className={diff !== 0 ? 'quantity-diff' : ''}>
                            <td>{item.codigoBarras}</td>
                            <td>{item.nombreProducto}</td>
                            <td>{item.cantidad}</td>
                            <td><strong>{item.cantidadConfirmada}</strong></td>
                            <td className={getDiffClassName(diff)}>
                              {formatDiff(diff)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const totalDiff = getTotalConfirmedItems(order.items) - getTotalItems(order.items);
                        return (
                          <tr>
                            <td colSpan="2"><strong>Total</strong></td>
                            <td><strong>{getTotalItems(order.items)}</strong></td>
                            <td><strong>{getTotalConfirmedItems(order.items)}</strong></td>
                            <td className={getDiffClassName(totalDiff)}>
                              <strong>{formatDiff(totalDiff)}</strong>
                            </td>
                          </tr>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Status History Section */}
          {expandedOrders[order.id] && (
            <div className="order-history-section">
              <button 
                onClick={() => toggleHistory(order.id)} 
                className="btn btn-small btn-secondary"
              >
                {historyData[order.id] ? 'Historial de Estados' : 'Ver Historial de Estados'}
              </button>
              
              {loadingHistory[order.id] && <p>Cargando historial...</p>}
              
              {historyData[order.id] && historyData[order.id].length > 0 && (
                <div className="history-list">
                  <h4>Historial de Cambios de Estado</h4>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Estado Anterior</th>
                        <th>Nuevo Estado</th>
                        <th>Usuario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyData[order.id].map((history, index) => (
                        <tr key={index}>
                          <td>{formatDate(history.changedAt)}</td>
                          <td>{getStatusLabel(history.previousStatus)}</td>
                          <td>{getStatusLabel(history.newStatus)}</td>
                          <td>{history.changedByNombre} ({history.changedByUsername})</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {historyData[order.id] && historyData[order.id].length === 0 && (
                <p className="no-history">No hay cambios de estado registrados</p>
              )}
            </div>
          )}

          {isDeposito && (
            <div className="order-actions">
              {order.status === 'PENDIENTE' && (
                <button
                  onClick={() => onStatusUpdate(order.id, 'EN_PROCESO')}
                  className="btn btn-warning"
                >
                  Marcar En Proceso
                </button>
              )}
              {order.status === 'EN_PROCESO' && (
                <button
                  onClick={(e) => { e.stopPropagation(); openConfirmModal(order); }}
                  className="btn btn-success"
                >
                  Confirmar Cantidades y Enviar
                </button>
              )}
              {(order.status === 'ENVIADA' || order.status === 'RECIBIDA') && (
                <span className="completed-label">✓ Completada</span>
              )}
            </div>
          )}

          {isSupermercado && order.status === 'ENVIADA' && (
            <div className="order-actions">
              <button
                onClick={() => onStatusUpdate(order.id, 'RECIBIDA')}
                className="btn btn-success"
              >
                Marcar Recibida
              </button>
            </div>
          )}
          
          {isSupermercado && order.status === 'RECIBIDA' && (
            <div className="order-actions">
              <span className="completed-label">✓ Recibida</span>
            </div>
          )}
        </div>
      ))}

      {/* Confirmation Modal for Deposito */}
      {confirmModal && (
        <div className="modal-overlay">
          <div className="modal-content confirm-modal">
            <div className="modal-header">
              <h3>Confirmar Cantidades - Orden #{confirmModal.id}</h3>
              <button onClick={closeConfirmModal} className="btn-close">&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Confirme las cantidades reales que se enviarán. Puede modificar las cantidades si es necesario.
              </p>
              <table className="confirm-items-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Cant. Pedida</th>
                    <th>Cant. a Enviar</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmModal.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.codigoBarras}</td>
                      <td>{item.nombreProducto}</td>
                      <td>{item.cantidad}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={confirmedQuantities[item.id] === '' ? '' : (confirmedQuantities[item.id] || 0)}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          onBlur={(e) => {
                            // Ensure valid quantity on blur
                            if (e.target.value === '') {
                              handleQuantityChange(item.id, '0');
                            }
                          }}
                          className="quantity-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button onClick={closeConfirmModal} className="btn btn-secondary">
                Cancelar
              </button>
              <button onClick={handleConfirmAndSend} className="btn btn-success">
                Confirmar y Marcar Enviada
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderList;
